// "How it's helping" — the receipts feed. Each card is one turn where per-prompt recall
// SURFACED a remembered fact and the assistant then CITED it (surfaced∩cited = an "assist").
// Data: GET /me/assist-log (newest-first events + hydrated node claims). In local dev, if the
// endpoint isn't reachable yet, falls back to a real prod snapshot so the UI is reviewable.
import { useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, CircularProgress, Tooltip, Typography } from "@mui/material";
import { assistStats, creditedOnTurn, sessionsPulled, formatTimeSaved } from "../utils/assistStats";
import { C, CARD, CARD_TITLE, MICRO_LABEL, SectionHeader, TimeChip } from "./memoryTheme";

const CLIENT_NAMES = { claude: "Claude Code", cursor: "Cursor", codex: "Codex" };

// The 3-stat mini-dashboard at the top of Impact. Three real, verifiable numbers — no trend lines
// (a rising line conflates usage-volume with value). The hours figure is flagged "EST." because it's
// a conservative estimate, not a count; the other two are hard counts from the retained window.
function StatCard({ label, value, unit, note, est }) {
  return (
    <Box sx={{ ...CARD, p: "18px 18px 16px" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        <Typography sx={{ ...MICRO_LABEL }}>{label}</Typography>
        {est && (
          <Box component="span" sx={{ fontSize: 9.5, fontWeight: 700, letterSpacing: 0.3,
                 color: "#9a7b2e", bgcolor: "#fbf3dc", border: "1px solid #efe0ad",
                 borderRadius: "4px", px: 0.625, py: "1px" }}>EST.</Box>
        )}
      </Box>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.875, mt: 1, lineHeight: 1 }}>
        <Typography sx={{ fontSize: 38, fontWeight: 700, color: C.tealDeep,
               fontVariantNumeric: "tabular-nums", lineHeight: 1 }}>{value}</Typography>
        {unit && <Typography sx={{ fontSize: 15, fontWeight: 600, color: C.muted }}>{unit}</Typography>}
      </Box>
      <Typography sx={{ fontSize: 11.5, color: C.muted, mt: 1.25, lineHeight: 1.45 }}>{note}</Typography>
    </Box>
  );
}

function StatDashboard({ stats, surfaced, lifetime }) {
  const assisted = stats.helpedTurns || 0;
  const xsession = stats.xsessionTurns || 0;
  const saved = formatTimeSaved(stats.hoursSaved);
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
               gap: 1.5, mb: 3 }}>
      <StatCard label="Turns helped" value={assisted} unit={`/ ${surfaced}`}
                note={lifetime ? "of your turns, memory cited a remembered fact"
                               : "of turns where it had something relevant to offer"} />
      <StatCard label="Across sessions" value={xsession} unit="turns"
                note="pulled facts from earlier sessions — not in the current chat" />
      {saved.show && (
        <StatCard label="Re-explaining saved" value={saved.value} unit={saved.unit} est
                  note="context you didn't have to restate (conservative floor)" />
      )}
    </Box>
  );
}

// Amber "update available" banner — the loud, interrupting nudge. We only show it when the user has
// NO current client (everything behind, or a pre-versioning build that hasn't reported). In a mixed
// state — at least one client on the latest — the user is clearly being served fine, so we stay quiet
// and let Connections carry it. Every version, current or stale, is listed there; this page shows
// only the case where capture is degraded for everything you run.
function UpgradeBanner({ plugin }) {
  if (!plugin?.outdated) return null;
  const clients = Object.entries(plugin.clients || {});
  // Mixed state: a current client exists → demote to the footer, don't alarm.
  if (clients.some(([, c]) => !c?.outdated)) return null;
  const latest = plugin.latest;
  const behind = clients
    .filter(([, c]) => c?.outdated)
    .map(([k, c]) => `${CLIENT_NAMES[k] || k} v${c.version}`);
  const detail = behind.length
    ? `You're on ${behind.join(" and ")} — the latest is v${latest}. Update to capture the most accurate receipts.`
    : `You're on a version older than v${latest} — update to capture the most accurate receipts.`;
  return (
    <Box sx={{ bgcolor: "#fff8ec", border: "1px solid #f0dca8", borderLeft: "3px solid #d9a93a",
               borderRadius: 2, px: 2, py: 1.5, mb: 2.5 }}>
      <Typography sx={{ color: "#7a5d10", fontSize: 13.5, fontWeight: 700, mb: 0.25 }}>
        Plugin update available
      </Typography>
      <Typography sx={{ color: "#8a6d1f", fontSize: 12.5, lineHeight: 1.5 }}>
        {detail}{" "}
        <Box component={RouterLink} to="/connect"
             sx={{ color: "#9a6a0f", fontWeight: 700, textDecoration: "underline", whiteSpace: "nowrap" }}>
          Update on Connections →
        </Box>
      </Typography>
      <Typography sx={{ color: "#a8893f", fontSize: 11.5, mt: 0.75,
                        pt: 0.75, borderTop: "1px solid #f0e3c2", display: "flex", gap: 0.5 }}>
        <Box component="span" aria-hidden>↻</Box>
        <span>Just updated? Send one message in your assistant — your version refreshes after the next turn.</span>
      </Typography>
    </Box>
  );
}

function relativeTime(ts) {
  if (!ts) return "";
  const then = new Date(ts).getTime();
  if (Number.isNaN(then)) return "";
  const s = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (s < 60) return "just now";
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return d === 1 ? "yesterday" : `${d}d ago`;
}

// One remembered fact the answer used: its claim text + a clickable node id.
function FactRow({ id, node, kind }) {
  const claim = node?.claim || "(this fact is no longer in your tree)";
  const subject = node?.subject_key || "";
  const badge =
    kind === "xsession" ? { label: "across sessions", color: "#1f8a78", bg: "rgba(31,138,120,0.12)" }
    : kind === "xsegment" ? { label: "across a compaction", color: "#7c4dd6", bg: "rgba(124,77,214,0.12)" }
    : kind === "dormant" ? { label: "earlier this session", color: "#7c4dd6", bg: "rgba(124,77,214,0.12)" }
    : null;
  return (
    <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start", py: 0.75 }}>
      <Box sx={{ mt: "5px", width: 6, height: 6, borderRadius: "50%", bgcolor: C.teal, flexShrink: 0 }} />
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ color: C.body, fontSize: 13.5, lineHeight: 1.5 }}>
          {claim}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5, flexWrap: "wrap" }}>
          <Tooltip title={subject || ""} placement="top" arrow>
            <Box component="span" sx={{ color: C.faint, fontSize: 11, fontFamily: "monospace" }}>
              [{id}]
            </Box>
          </Tooltip>
          {badge && (
            <Box component="span"
                 sx={{ display: "inline-flex", alignItems: "center", gap: 0.375,
                       bgcolor: badge.bg, color: badge.color, fontSize: 10, fontWeight: 700,
                       letterSpacing: 0.4, textTransform: "uppercase",
                       px: 0.75, py: "2px", borderRadius: "999px", lineHeight: 1.4 }}>
              ↩ {badge.label}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

function Receipt({ ev, nodes }) {
  const credited = creditedOnTurn(ev, nodes);
  const assisted = credited.map((f) => f.id);
  const kindById = Object.fromEntries(credited.map((f) => [f.id, f.kind]));
  const nSessions = sessionsPulled(ev, nodes);
  return (
    <Box sx={{ ...CARD, p: 2, mb: 1.5 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, mb: 1 }}>
        <Typography sx={{ ...CARD_TITLE, flex: 1, minWidth: 0 }}>
          {ev.query || "(a turn in your session)"}
        </Typography>
        <TimeChip>{relativeTime(ev.ts)}</TimeChip>
      </Box>
      <Typography sx={{ color: C.muted, fontSize: 11.5, mb: 0.5 }}>
        Memory surfaced {assisted.length} fact{assisted.length === 1 ? "" : "s"} the answer used
      </Typography>
      {assisted.map((id) => (
        <FactRow key={id} id={id} node={nodes[id]} kind={kindById[id]} />
      ))}
      {/* Breadth reveal: how many distinct sessions these facts were last touched in — the concrete
          network/compounding signal. Only shown when it spans more than one session. */}
      {nSessions > 1 && (
        <Box sx={{ mt: 1.25, pt: 1.25, borderTop: `1px dashed ${C.line}` }}>
          <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.625,
                 color: C.tealDeep, fontSize: 12, fontWeight: 600 }}>
            <Box component="span" sx={{ fontSize: 13 }}>⇲</Box>
            Pulled from {nSessions} separate sessions
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default function MemoryReceipts({ data, loading, usedFixture }) {
  // The feed shows the VALUE moments — turns where memory carried a fact native context couldn't
  // have (cross-session, or same-session captured ≥DORMANT_TURNS ago), not every cited turn.
  const receipts = useMemo(
    () => (data?.events || []).filter((e) => creditedOnTurn(e, data?.nodes).length > 0),
    [data]
  );
  const totals = data?.totals || {};
  const plugin = data?.plugin || null;
  const surfacedTurns = totals.turns || 0;
  const stats = useMemo(() => assistStats(data?.events, data?.nodes, data?.counter), [data]);
  // Denominator = all-active turns (lifetime, cap-proof) once the counter is seeded; else the
  // surfaced-turn window count. Guarded so the ratio can never render > 100%.
  const lifetime = data?.counter != null;
  const active = data?.active_turns;
  const denom = active != null && active >= (stats.helpedTurns || 0) ? active : surfacedTurns;

  if (loading) {
    return (
      <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress size={26} sx={{ color: C.teal }} />
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100%", overflowY: "auto", bgcolor: C.canvas }}>
      <Box sx={{ maxWidth: 760, mx: "auto", px: { xs: 2, md: 3 }, py: 3 }}>
        <UpgradeBanner plugin={plugin} />

        <SectionHeader title="Impact" subtitle="What memory saved you, turn by turn" />

        {/* Three real, verifiable numbers — no trend lines (a rising line conflates usage with value). */}
        {denom > 0 && <StatDashboard stats={stats} surfaced={denom} lifetime={lifetime} />}

        {receipts.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 8, color: C.faint }}>
            {/* Two different empty states, because "nothing here" has two different causes.
                The stat card above counts ANY-CITED turns; this feed only shows the turns where
                memory carried a fact native context couldn't have (see creditedOnTurn). A user
                whose every cited fact was still in-window therefore reads a non-zero "Turns
                helped" directly above "No assists yet" — a flat contradiction that makes the
                page look broken. Name the reason instead. */}
            {stats.helpedTurns > 0 ? (
              <>
                <Typography sx={{ fontSize: 14, color: C.muted }}>
                  Memory was cited on {stats.helpedTurns} turn{stats.helpedTurns === 1 ? "" : "s"},
                  but those facts were still in your context window.
                </Typography>
                <Typography sx={{ fontSize: 12.5, mt: 1.25, maxWidth: 430, mx: "auto", lineHeight: 1.55 }}>
                  Receipts show up here once memory carries a fact your assistant had otherwise
                  lost — from an earlier session, across a compaction, or from far enough back in
                  this one.
                </Typography>
              </>
            ) : (
              <Typography sx={{ fontSize: 14 }}>
                No assists yet. As you work, when memory surfaces a fact your assistant uses,
                it'll show up here.
              </Typography>
            )}
          </Box>
        ) : (
          receipts.map((ev, i) => (
            <Receipt key={`${ev.session_id}-${ev.turn}-${i}`} ev={ev} nodes={data?.nodes || {}} />
          ))
        )}


        {usedFixture && (
          <Typography sx={{ color: "#b0b5bd", fontSize: 10.5, mt: 2, textAlign: "center" }}>
            preview data (local dev — /me/assist-log not reachable)
          </Typography>
        )}
      </Box>
    </Box>
  );
}
